import {
  Configuration,
  PaymentStripeBookingServiceApi,
} from '@ennismore/payment-stripe-api-client';
import { Paymentv1PaymentMethod } from '@ennismore/payment-stripe-api-client/generated-src';
import type {
  V3PrePaymentRequest,
  V3PrePaymentResponse,
} from '@ennismore/payment-stripe-api-client/generated-src';
import { z } from 'zod';

import { getClientHeaders } from '@/api/headers';
import { buildBaseUrl } from '@/api/helpers/build-base-url';
import { type EnnismoreAPIClientConfig } from '@/api/interfaces';

const prePaymentResponseSchema = z.object({
  clientSecret: z.string(),
  method: z.nativeEnum(Paymentv1PaymentMethod),
  created: z.string(),
});

export abstract class PaymentServiceClient {
  config: Configuration;

  constructor(protected readonly credentials: EnnismoreAPIClientConfig) {
    const baseUrlWithoutV2 = credentials.baseUrl.replace('/v2', '');
    this.config = new Configuration({
      basePath: buildBaseUrl(baseUrlWithoutV2, {
        servicePrefix: 'payment',
      }),
    });
  }

  abstract createPaymentToken(
    request: V3PrePaymentRequest
  ): Promise<V3PrePaymentResponse>;
}

export class OpenAPIV3PaymentServiceClient extends PaymentServiceClient {
  get client() {
    return new PaymentStripeBookingServiceApi(this.config);
  }

  async createPaymentToken(
    request: V3PrePaymentRequest
  ): Promise<V3PrePaymentResponse> {
    // Map a domain-level StripePrePaymentRequest to v1PrePaymentRequest

    const response = await this.client.prePayment(
      {
        ...request,
        metadata: {
          ...request.metadata,
          cot: request.metadata.cot,
          flexyTime: {
            checkIn: request.metadata.flexyTime?.checkIn,
            checkOut: request.metadata.flexyTime?.checkOut,
          },
          usaState: request.metadata.usaState,
          country: request.metadata.country,
        },
      },
      {
        headers: {
          'x-api-key': this.credentials.apiKey,
          ...getClientHeaders(this.credentials),
        },
      }
    );
    return prePaymentResponseSchema.parse({
      ...response.data,
      method: response.data.method,
    });
  }
}

export const createPaymentServiceClient = (
  config: EnnismoreAPIClientConfig
): PaymentServiceClient => new OpenAPIV3PaymentServiceClient(config);
